import { CdkDragDrop } from '@angular/cdk/drag-drop';
import {
  Component,
  Input,
  Output,
  EventEmitter,
  ViewChild,
  ElementRef,
  OnInit,
  OnDestroy,
  ViewChildren,
  QueryList,
  OnChanges,
  SimpleChanges,
  ChangeDetectorRef,
} from '@angular/core';
import {
  UntypedFormGroup,
  UntypedFormArray,
  AbstractControl,
  UntypedFormControl,
  UntypedFormBuilder,
} from '@angular/forms';
import { MatLegacyAutocompleteTrigger as MatAutocompleteTrigger } from '@angular/material/legacy-autocomplete';
import { MatLegacyDialog as MatDialog } from '@angular/material/legacy-dialog';
import { MatLegacySnackBar as MatSnackBar } from '@angular/material/legacy-snack-bar';
import { Observable, Subject } from 'rxjs';
import { skip, takeUntil, tap } from 'rxjs/operators';

import { ECustomFieldType } from '../../../custom-field';
import {
  fadeOutBackgroundAnimation,
  slideInAnimation,
} from '../../../shared/animations/animation';
import { TableAnimateRowDirective } from '../../../shared/directives/table-animate-row.directive';
import { IColumnConfig } from '../../../shared/interfaces/table.interface';
import {
  trackByControlValue,
  trackById,
  trackByLabel,
  trackByValue,
} from '../../../shared/util';
import { ERfqStatus } from '../../interfaces/rfq.interface';

import { ICompany } from './../../../client/interfaces/client-company.interface';
import {
  ELineItemRowType,
  EWarehouseDefaultGroup,
} from './../../../invoice/interfaces/invoice.interface';
import { ISalesOrder } from './../../../order/interfaces/order.interface';
import { IPrice } from './../../../pricing/interfaces/pricing.interface';
import { SalesOrderConfig } from './../../../sales-order/configs/sales-order.config';
import { ESalesOrderLineItemStatus } from './../../../sales-order/interfaces/sales-order.interface';
import { DialogConfig } from './../../../shared/configs/dialog.config';
import { SnackBarConfig } from './../../../shared/configs/snackbar.config';
import { PricingApiService } from './../../../shared/services/api/pricing/pricing-api.service';
import { RfqApiService } from './../../../shared/services/api/rfq/rfq-api.service';
import { RfqUtilService } from './../../../shared/services/util/rfq-util.service';
import { UiConfirmationDialogComponent } from './../../../ui/components/ui-confirmation-dialog/ui-confirmation-dialog.component';
import { UiMessageDialogComponent } from './../../../ui/components/ui-message-dialog/ui-message-dialog.component';
import { isEqual } from 'lodash';

@Component({
  selector: 'lum-rfq-table-form-v2',
  templateUrl: './rfq-table-form-v2.component.html',
  styleUrls: ['./rfq-table-form-v2.component.scss'],
  animations: [fadeOutBackgroundAnimation, slideInAnimation],
})
export class RfqTableFormV2Component implements OnInit, OnChanges, OnDestroy {
  @Input()
  form :UntypedFormGroup;

  @Input()
  searchableFields :string[] = [];

  @Input()
  searchStr :string = '';

  @Input()
  fromTransferOrder :boolean = false;

  @Input()
  columns :IColumnConfig[] = [];

  @Input()
  lineItemsEditDisabled :boolean = false;

  @Input()
  currentSalesOrder :ISalesOrder;

  @Input()
  hasApprovalFlow :boolean = false;

  @Output()
  removeLineItem = new EventEmitter<number>();

  @Output()
  addRowItems = new EventEmitter<number>();

  @Output()
  addRowItemSubTotal = new EventEmitter<number | null>();

  @Output()
  searchRfq = new EventEmitter<any>(); // TODO: Create interface

  @Output()
  enterRfqEvent = new EventEmitter<{
    item :AbstractControl; // TODO: Create interface
    index :number;
    searchStr :string;
  }>();

  @Output()
  selectRfq = new EventEmitter<{
    item :any; // TODO: Create interface
    tempId :number;
  }>();

  @Output()
  scrollPaginate = new EventEmitter<boolean>();

  @Output()
  recalculateSubtotals = new EventEmitter();

  @ViewChild(MatAutocompleteTrigger)
  autoCompleteTrigger! :MatAutocompleteTrigger;

  @ViewChildren(TableAnimateRowDirective)
  rows :QueryList<TableAnimateRowDirective>;

  rfqs :any[] = [];
  rfqsBySku :any = {};
  pricingByRfq :{ [key :string] :IPrice } = {};
  isDragged :boolean = false;
  selectAll :UntypedFormControl = new UntypedFormControl(false);
  inputsNodeList :NodeListOf<Element>;
  isFromScrollEvent :boolean = false;
  isFromEnterEvent :boolean = false;
  // isFromScanMode: boolean = false;

  querySelectorStr :string =
    '.lum-table input.table__search-skus-input:not([type="checkbox"]):not([disabled]):not([readonly])';

  subTotalIndex :number | null = null;
  sortState = { column: '', direction: 'asc' };
  duplicateForm :UntypedFormGroup;
  eLineItemRowType = ELineItemRowType;

  queryParams :any = {
    relations: [
      'category',
      'subcategory',
      'basePrice',
      'tags.tag_details',
      'images',
      'locationStock',
      'variantParent.category',
      'variantParent.subcategory',
      'variantParent.tags.tag_details',
      'variantParent.unitOfMeasures',
      'rfqAttachments',
      'unitOfMeasures',
      'quotations',
    ].join(','),
    sellable: '1',
    warehouse_group_id: EWarehouseDefaultGroup.DEFAULT.toString(),
    per_page: 50,
    offset: 0,
  };
  pricingByRfq$ :Observable<{
    [key :string] :IPrice;
  }>;

  lineItemStatusKeyLabel = SalesOrderConfig.lineItemStatusKeyLabel;
  salesOrderLineItemStatus = ESalesOrderLineItemStatus;

  trackByControlValue = trackByControlValue;
  trackById = trackById;
  trackByLabel = trackByLabel;
  trackByValue = trackByValue;

  private onDestroy$ = new Subject();
  customFieldColumns :IColumnConfig[] = [];
  customFieldType = ECustomFieldType;

  constructor(
    private cd :ChangeDetectorRef,
    private dialog :MatDialog,
    private rfqApiService :RfqApiService,
    private rfqUtilService :RfqUtilService,
    private pricingApiService :PricingApiService,
    private snackBar :MatSnackBar,
    private el :ElementRef,
    private fb :UntypedFormBuilder,
  ) {}

  ngOnInit() :void {
    this.listentToDOMNodeInsertedEvent();
    this.getFromService();
    this.getRfqs().subscribe();
    this.getPricingByRfq().subscribe();

    if (this.currentSalesOrder) {
      this.#listenToQuantityAndEstimatedShipDateChanges();
    }

    this.duplicateForm = this.form;
    this.getFilteredCustomFieldColumns();
  }

  ngOnChanges(changes :SimpleChanges) :void {
    if (changes['form']) {
      this.duplicateForm = this.form;
      if (!changes['form'].firstChange && this.currentSalesOrder) {
        this.#listenToQuantityAndEstimatedShipDateChanges();
      }
    }

    if (changes['columns']) {
      const prev = changes['columns'].previousValue;
      const curr = changes['columns'].currentValue;

      if (!isEqual(prev, curr)) {
        if (this.customFieldColumns.length === 0) {
          this.getFilteredCustomFieldColumns();
        } else {
          this.updateCustomFieldColumnsVisibility();
        }
      }
    }
  }

  ngOnDestroy() :void {
    this.onDestroy$.next(null);
    this.onDestroy$.complete();
  }

  drop(event :CdkDragDrop<string[]>) {
    const { previousIndex, currentIndex } = event;
    const control = this.lineItemsFormArray.at(previousIndex);
    const controlCurrent = this.lineItemsFormArray.at(currentIndex);

    if (
      (previousIndex === 0 &&
        controlCurrent.value?.row_type === ELineItemRowType.SUB_TOTAL) ||
      control.value?.row_type === ELineItemRowType.SUB_TOTAL
    ) {
      this.dialog.open(UiMessageDialogComponent, {
        data: {
          title: 'Invalid Action',
          message:
            'Row subtotal cannot be placed on top or next to another subtotal row.',
        },
        width: DialogConfig.sizes.small,
      });

      return;
    }

    this.lineItemsFormArray.removeAt(previousIndex);
    this.lineItemsFormArray.insert(currentIndex, control);
    this.recalculateSubtotals.emit();
  }

  onSelectAll() :void {
    const selectedValue = this.selectAll.value;
    this.lineItemsFormArray.controls.forEach((control) =>
      control.get('selected')?.setValue(!selectedValue),
    );
  }

  onSelectSku(item :any, itemId :number) :void {
    const itemGroup :UntypedFormGroup = this.rfqUtilService.getItemFormGroup(item);
    const index = this.lineItemsFormArray.controls.findIndex(
      (control :AbstractControl) => control.value.item_id === itemId,
    );

    this.lineItemsFormArray.setControl(index, itemGroup);

    this.pricingApiService.getByRfq({
      rfqId: itemGroup.value?.item_id?.toString(),
      quantity: itemGroup.value?.quantity || 0,
      uomId: itemGroup.value?.ordered_uom_id?.toString(),
      businessAccountId: this.company?.id?.toString() || '',
      priceLevelId: null,
    });

    const targetIndex = Array.from(this.inputRefsNativeElements).findIndex(
      (input :Element, index :number, obj :Element[]) => {
        // Check if input id matches the itemId
        return input.id === `id-${itemId}`;
      },
    );

    this.focusToNextInput({ index: targetIndex });
    this.rfqs = [];
  }

  onEnterRfq(event :Event, item :AbstractControl, index :number) :void {
    this.isFromEnterEvent = true;
    this.isFromScrollEvent = false;

    const inputValue = (event.target as HTMLInputElement).value;

    // Prevent default Enter behavior
    event.preventDefault();

    // Close the autocomplete panel
    this.autoCompleteTrigger.closePanel();
    if (inputValue) {
      this.rfqsBySku[inputValue.toLowerCase()] = { item, index };
    }

    const targetIndex = Array.from(this.inputRefsNativeElements).findIndex(
      (input :Element, index :number, obj :Element[]) => {
        // Check if input id matches the itemId
        return input.id === `id-${item.value.item_id}`;
      },
    );

    this.focusToNextInput({ index: targetIndex, isFromEnterEvent: true });
  }

  onScrollPaginate() :void {
    this.isFromScrollEvent = true;
    this.queryParams.offset += this.queryParams.per_page;
    this.rfqApiService.search({
      ...this.queryParams,
    });
  }

  onSearchRfq(sku :string) :void {
    this.rfqs = [];
    this.isFromScrollEvent = false;
    this.isFromEnterEvent = false;
    // this.isFromScanMode = false;

    this.queryParams.sku = sku;
    this.rfqApiService.search({
      ...this.queryParams,
    });
  }

  displayFnRfq(option :any) :string {
    return option && option.sku ? `${option.product_name}[${option.sku}]` : '';
  }

  onRemoveLineItem(index :number) :void {
    this.rows.toArray()[index].slideOut();

    this.lineItemsFormArray.removeAt(index);
    setTimeout(() => {
      // this.onRecalculateSubtotals();
      // this.cd.detectChanges();
    }, 500);
    // setTimeout(() => {
    //   this.removeLineItem.emit(index);
    // }, 1000);
  }

  onAddSubtotalRow(index :number) :void {
    this.subTotalIndex = index;
    this.addRowItemSubTotal.emit(index);
  }

  onConfirmSort(column :string) :void {
    // Check if there are subtotal rows
    const subtotalRows = this.lineItemsFormArray.controls.filter((control) =>
      control.value?.row_type === ELineItemRowType.SUB_TOTAL ? control : null,
    );

    if (subtotalRows.length > 0) {
      this.dialog
        .open(UiConfirmationDialogComponent, {
          data: {
            title: 'Confirm Action',
            message:
              'Sorting will remove all subtotal rows. Do you want to continue?',
          },
          width: DialogConfig.sizes.small,
        })
        .afterClosed()
        .pipe(takeUntil(this.onDestroy$))
        .subscribe((result) => {
          if (result) {
            this.sortFormArray(column);
          }
        });
    } else {
      this.sortFormArray(column);
    }
  }

  sortFormArray(column :string) :void {
    // Check if there are subtotal rows
    const subtotalRows = this.lineItemsFormArray.controls.filter((control) =>
      control.value?.row_type === ELineItemRowType.SUB_TOTAL ? control : null,
    );

    const formArray = this.sortedLineItemsFormArray;

    if (this.sortState.column === column) {
      if (this.sortState.direction === 'asc') {
        this.sortState.direction = 'desc';
      } else if (this.sortState.direction === 'desc') {
        // Resetting to normal (original order)
        this.sortState.direction = '';
        this.sortState.column = '';
        this.resetToOriginalOrder();

        return;
      }
    } else {
      this.sortState.column = column;
      this.sortState.direction = 'asc';
    }

    const sortedFormGroups = formArray.controls.sort((a, b) => {
      let aValue = a.get(column)?.value;
      let bValue = b.get(column)?.value;

      // if sku and name is null or empty, then do not include on sorting
      if ((!a.value?.sku && !a.value?.name) || (!b.value?.sku && !b.value?.name)) {
        return 0;
      }

      switch (column) {
        case 'ordered_uom_id':
          aValue = a
            .get('unit_of_measures')
            ?.value?.find((uom :any) => uom.id === a.value?.ordered_uom_id)?.name;
          bValue = b
            .get('unit_of_measures')
            ?.value?.find((uom :any) => uom.id === b.value?.ordered_uom_id)?.name;
          break;

        case 'location_stock':
          if (!aValue) {
            aValue = ERfqStatus.UNAVAILABLE;
          }

          if (!bValue) {
            bValue = ERfqStatus.UNAVAILABLE;
          }

          const aAvailable =
            a.value?.location_stock?.qty_onhand -
            a.value?.location_stock?.qty_pending;

          const bAvailable =
            b.value?.location_stock?.qty_onhand -
            b.value?.location_stock?.qty_pending;

          const aLineQty = a.value?.quantity;
          const aLimitOos = a.value?.oss_limit_notify;

          const bLineQty = b.value?.quantity;
          const bLimitOos = b.value?.oss_limit_notify;

          if (aLimitOos > 0) {
            if (aAvailable <= aLimitOos || aAvailable - aLineQty <= aLimitOos) {
              aValue = ERfqStatus.UNAVAILABLE;
            }
          } else {
            if (aAvailable <= 0 || aLineQty >= aAvailable) {
              aValue = ERfqStatus.UNAVAILABLE;
            } else {
              aValue = ERfqStatus.AVAILABLE;
            }
          }

          if (bLimitOos > 0) {
            if (bAvailable <= bLimitOos || bAvailable - bLineQty <= bLimitOos) {
              bValue = ERfqStatus.UNAVAILABLE;
            }
          } else {
            if (bAvailable <= 0 || bLineQty >= bAvailable) {
              bValue = ERfqStatus.UNAVAILABLE;
            } else {
              bValue = ERfqStatus.AVAILABLE;
            }
          }

          break;

        case 'category':
          aValue = aValue?.product_category;
          bValue = bValue?.product_category;
          break;

        case 'subcategory':
          aValue = aValue?.sub_category;
          bValue = bValue?.sub_category;
          break;

        default:
          break;
      }

      // Handle null and ensure string comparison
      const aString = !aValue
        ? ''
        : typeof aValue === 'string'
          ? aValue.toString().toLowerCase()
          : aValue;
      const bString = !bValue
        ? ''
        : typeof bValue === 'string'
          ? bValue.toString().toLowerCase()
          : bValue;

      if (aString < bString) {
        return this.sortState.direction === 'asc' ? -1 : 1;
      } else if (aString > bString) {
        return this.sortState.direction === 'asc' ? 1 : -1;
      } else {
        return 0;
      }
    });

    // Reassign sorted form groups to the FormArray
    for (let i = 0; i < sortedFormGroups.length; i++) {
      formArray.setControl(i, sortedFormGroups[i]);
    }

    this.subTotalIndex = null;
    this.addRowItemSubTotal.emit(null);
  }

  private resetToOriginalOrder() :void {
    this.lineItemsFormArray.controls.forEach((control, index) => {
      this.sortedLineItemsFormArray.setControl(index, control);
    });
  }

  private listentToDOMNodeInsertedEvent() :void {
    // Attach event listener for new rows added to the table
    const table = document.querySelector('.lum-table') as HTMLTableElement;
    table.addEventListener('DOMNodeInserted', () => {
      this.setInputsNodeList();
    });
  }

  private setInputsNodeList() :void {
    const inputs = document.querySelectorAll(this.querySelectorStr);
    this.inputsNodeList = inputs;
  }

  private focusToNextInput(data :{
    index :number;
    isFromEnterEvent ?:boolean;
  }) :void {
    const { index, isFromEnterEvent } = data;
    let nextIndex = index + 1;

    if (this.inputRefsNativeElements.length === nextIndex) {
      this.addRowItems.emit(1);

      setTimeout(() => {
        const nextInput = this.inputRefsNativeElements[
          isFromEnterEvent ? nextIndex : nextIndex - 1
        ] as HTMLInputElement;
        nextInput.focus();
      }, 500);
    } else {
      while (nextIndex < this.inputRefsNativeElements?.length) {
        const nextInput = this.inputRefsNativeElements[
          nextIndex
        ] as HTMLInputElement;
        if (
          !nextInput.disabled &&
          !nextInput.readOnly &&
          nextInput.classList.contains('table__search-skus-input')
        ) {
          setTimeout(() => {
            nextInput.focus();
          }, 500);

          return;
        }
        nextIndex++;
      }
    }
  }

  private getRfqs() :Observable<any[]> {
    return this.rfqs$.pipe(
      skip(1),
      tap((rfqs :any[]) => {
        if (this.isFromScrollEvent) {
          this.rfqs = [...this.rfqs, ...rfqs];

          if (rfqs.length === 0) {
            this.snackBar.open('All SKUs successfully loaded.', 'Close', {
              duration: SnackBarConfig.DEFAULT.duration,
            });
          }
        } else {
          this.rfqs = rfqs;
          if (rfqs?.length === 0 && !this.isFromEnterEvent) {
            this.snackBar.open('No SKU found.', 'Fail', {
              duration: SnackBarConfig.DEFAULT.duration,
            });

            this.rfqs = [];
          } else if (this.isFromEnterEvent && rfqs?.length) {
            const rfq = rfqs[0];
            const rfqsBySku = this.rfqsBySku[rfq.sku?.toLowerCase()];

            if (rfqsBySku) {
              this.adddLineItem({ item: rfq, index: rfqsBySku.index });
              delete this.rfqsBySku[rfq.sku?.toLowerCase()];
            }

            this.rfqs = [];
          }

          // TODO: Handle this one later
          // else if (rfqs?.length === 1 && this.isFromScanMode) {
          //   this.adddLineItem({
          //     item: rfqs[0],
          //     insertPosition: 'top',
          //   });
          //   // this.scanSearchStr.setValue(null);
          //   this.rfqs = [];
          // }
        }
      }),
      takeUntil(this.onDestroy$),
    );
  }

  private getFromService() :void {
    this.pricingByRfq$ = this.pricingApiService.pricingByRfq$;
  }

  private getPricingByRfq() :Observable<{
    [key :string] :IPrice;
  }> {
    return this.pricingByRfq$.pipe(
      tap((pricingByRfq) => {
        this.pricingByRfq = pricingByRfq;
        Object.keys(pricingByRfq).forEach((itemId :string) => {
          this.updateLineItemPrice(pricingByRfq[itemId]);
        });
      }),
      takeUntil(this.onDestroy$),
    );
  }

  private updateLineItemPrice(rfqPricing :IPrice) :void {
    const items :any[] = this.lineItemsFormArray.value;
    const filteredIndexes = items
      .map((item, index) =>
        item.item_id === rfqPricing.requestedRfqId &&
        item.ordered_uom_id === rfqPricing.requestedUomId
          ? index
          : -1,
      )
      .filter((index) => index !== -1);

    if (filteredIndexes.length === 0) {
      return;
    }

    filteredIndexes.forEach((index) => {
      const item = items[index];
      const itemForm = this.lineItemsFormArray.at(index) as UntypedFormGroup;
      const unitPriceControl = itemForm.get('unit_price');
      const origUnitPriceControl = itemForm.get('original_unit_price');
      const netTotalAmountControl = itemForm.get('net_total_amount');

      origUnitPriceControl?.patchValue(
        Number(rfqPricing.wholesalePrice?.toFixed(2)),
      );

      netTotalAmountControl?.patchValue(
        Number(rfqPricing.wholesalePrice?.toFixed(2)) * item.quantity,
      );

      if (!item?.is_overridden) {
        unitPriceControl?.patchValue(Number(rfqPricing.wholesalePrice?.toFixed(2)));
      }
    });

    this.cd.detectChanges();
  }

  private adddLineItem(data :{
    item :any;
    index ?:number;
    insertPosition ?:'top' | 'bottom';
  }) :void {
    const { item, index, insertPosition } = data;
    const { quantity, default_selling_unit_id } = item;
    const ordered_uom = item.unit_of_measures.find(
      (uom :any) => uom.id === default_selling_unit_id,
    );

    const itemGroup = this.rfqUtilService.getItemFormGroup(item);

    if (index !== undefined) {
      this.lineItemsFormArray.setControl(index, itemGroup);
    } else {
      // Find the index where the empty/default values end
      let insertIndex = this.lineItemsFormArray.length;
      let emptyCount = 0;
      for (let i = this.lineItemsFormArray.length - 1; i >= 0; i--) {
        const control = this.lineItemsFormArray.at(i);
        const value = control.value;
        if (value.product_name !== '') {
          break;
        }
        emptyCount++;
      }
      if (emptyCount > 0) {
        insertIndex = this.lineItemsFormArray.length - emptyCount;
      }
      this.lineItemsFormArray.insert(
        insertPosition === 'top' ? 0 : insertIndex,
        itemGroup,
      );
    }

    this.pricingApiService.getByRfq({
      rfqId: item.id.toString(),
      quantity: quantity || 0,
      uomId: ordered_uom?.id?.toString(),
      businessAccountId: this.company?.id?.toString() || '',
      // priceLevelId: this.pricingLevelIdControl?.value || null,
    });
  }

  private getFilteredCustomFieldColumns() :void {
    this.customFieldColumns = this.columns.filter((column) => column.customField);
  }

  private updateCustomFieldColumnsVisibility() :void {
    this.customFieldColumns.forEach((customField) => {
      const updatedColumn = this.columns.find(
        (column) => column.id === customField.id,
      );
      if (updatedColumn && customField.visible !== updatedColumn.visible) {
        customField.visible = updatedColumn.visible;
      }
    });
  }

  // Create a private function that listen to quantity abd estimated ship date changes
  #listenToQuantityAndEstimatedShipDateChanges() :void {
    this.form
      .get('line_items')
      ?.valueChanges.pipe(takeUntil(this.onDestroy$))
      .subscribe((lineItems :any[]) => {
        lineItems.forEach((lineItem :any, index :number) => {
          const control = this.lineItemsFormArray.at(index);
          const quantityControl = control.get('quantity');
          const estimatedShipDateControl = control.get('estimated_ship_date');
          const lineItemStatusControl = control.get('line_item_status');
          const origItem = this.currentSalesOrder.orderItems.find(
            (item) => item.rfqId === lineItem.item_id,
          );

          // Check if quantity has changed
          if (quantityControl?.dirty) {
            if (lineItem.quantity === 0 && Number(origItem?.quantity) > 0) {
              lineItemStatusControl.patchValue(
                this.salesOrderLineItemStatus.REJECTED,
                {
                  emitEvent: false,
                },
              );
            } else if (Number(origItem?.quantity) !== lineItem.quantity) {
              lineItemStatusControl.patchValue(
                this.salesOrderLineItemStatus.QUANTITY_CHANGED,
                {
                  emitEvent: false,
                },
              );
            } else if (Number(origItem?.quantity) === lineItem.quantity) {
              lineItemStatusControl.patchValue(
                this.salesOrderLineItemStatus.ACCEPTED,
                {
                  emitEvent: false,
                },
              );
            }
            quantityControl.markAsPristine();
          }

          // Check if estimated ship date has changed
          if (estimatedShipDateControl?.dirty) {
            if (origItem?.estimatedShipDate !== lineItem.estimated_ship_date) {
              lineItemStatusControl.patchValue(
                this.salesOrderLineItemStatus.BACKORDERED,
                {
                  emitEvent: false,
                },
              );
            } else {
              lineItemStatusControl.patchValue(
                this.salesOrderLineItemStatus.ACCEPTED,
                {
                  emitEvent: false,
                },
              );
            }
            estimatedShipDateControl.markAsPristine();
          }
        });
      });
  }

  get company() :ICompany {
    return this.form.get('company')?.value;
  }

  get lineItemsFormArray() :UntypedFormArray {
    return this.form.get('line_items') as UntypedFormArray;
  }

  get sortedLineItemsFormArray() :UntypedFormArray {
    return this.duplicateForm?.get('line_items') as UntypedFormArray;
  }

  get isOverride() :boolean {
    return this.form.get('overridden')?.value || 0;
  }

  get inputRefsNativeElements() :HTMLInputElement[] {
    const inputElements = this.el.nativeElement.querySelectorAll('.rfq-item-input');

    return Array.from(inputElements) as HTMLInputElement[];
  }

  get displayColumns() :{
    [key :string] :{ visible :boolean; label :string };
  } {
    return this.columns.reduce(
      (acc, { id, visible, label }) => {
        acc[id] = { visible, label };

        return acc;
      },
      {} as { [key :string] :{ visible :boolean; label :string } },
    );
  }

  get rfqs$() :Observable<any[]> {
    return this.rfqApiService.rfqs$;
  }

  get loadingRfqs$() :Observable<boolean> {
    return this.rfqApiService.loading$;
  }

  get channelFormWarehouseGroupId() :number | null {
    return this.form.get('channel').value?.warehouseGroupId ?? null;
  }

  onCustomFieldChange(
    formGroup :AbstractControl,
    columnId :number,
    value :any,
  ) :void {
    const customFieldValuesGroup = formGroup.get(
      'customFieldValues',
    ) as UntypedFormGroup;
    if (!customFieldValuesGroup) {
      return;
    }

    const columnKey = columnId.toString();
    let fieldControl = customFieldValuesGroup.get(columnKey);
    if (!fieldControl) {
      fieldControl = this.fb.group({
        id: [columnId],
        value: [null],
      });
      customFieldValuesGroup.addControl(columnKey, fieldControl);
    }

    fieldControl.get('value')?.patchValue(value);
    customFieldValuesGroup.markAsDirty();
  }
}
